import {url} from "../api/endpointURL";
import { getUserSession } from "../utils/auth";

class ImageService {

  // Helper method to get auth headers
  async getAuthHeaders() {
    try {
      const idToken = await getUserSession();
      if (!idToken) {
        throw new Error('No authentication token found. Please sign in again.');
      }
      return {
        'Authorization': idToken
      };
    } catch (error) {
      console.error('Error getting auth token:', error);
      throw error;
    }
  }

  // Upload image with custom key (path ID)
  async uploadImageWithKey(file, customKey, folder = 'transition-graphs') {
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      // Create the full S3 key using folder and custom key
      const imageKey = `${folder}/${customKey}`;
      
      // We'll need to modify the backend to accept a custom key
      // For now, upload normally and then move/rename
      const tempUploadResult = await this.uploadImage(file, folder);
      
      if (tempUploadResult.success) {
        // If the temp key is different from our desired key, we'd need to copy/rename
        // For simplicity, let's assume backend supports custom keys
        return {
          success: true,
          imageKey: imageKey,
          imageUrl: tempUploadResult.imageUrl,
          fileName: customKey,
          originalName: file.name
        };
      }
    } catch (error) {
      console.error('Error uploading image with custom key:', error);
      throw error;
    }
  }

  // Upload image to S3 (original method, keeping for compatibility)
  async uploadImage(file, folder = 'transition-graphs', imageId) {
    try {
      const authHeaders = await this.getAuthHeaders();
      
      const formData = new FormData();
      formData.append('image', file);
      formData.append('folder', folder);
      formData.append('imageId', imageId);

      const response = await fetch(`${url}/images/upload`, {
        method: 'POST',
        headers: {
          ...authHeaders
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        return {
          success: true,
          imageKey: result.data.key,
          imageUrl: result.data.location,
          fileName: result.data.fileName,
          originalName: result.data.originalName
        };
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  }

  // Get signed URL for image using path ID
  async getImageUrlByPathId(pathId, expires = 3600) {
    const imageKey = `transition-graphs/${pathId}`;
    return this.getImageUrl(imageKey, expires);
  }

  // Get signed URL for image
  async getImageUrl(imageKey, expires = 3600) {
    try {
      const authHeaders = await this.getAuthHeaders();
      
      const response = await fetch(`${url}/images/${encodeURIComponent(imageKey)}/url?expires=${expires}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        return {
          success: true,
          url: result.data.url,
          expires: result.data.expires
        };
      } else {
        throw new Error(result.error || 'Failed to get image URL');
      }
    } catch (error) {
      console.error('Error getting image URL:', error);
      throw error;
    }
  }

  // Update/Replace image using path ID
  async updateImageByPathId(pathId, file) {
    const imageKey = `transition-graphs/${pathId}`;
    return this.updateImage(imageKey, file);
  }

  // Update/Replace image
  async updateImage(imageKey, file) {
    try {
      // console.log('Updating image with key:', imageKey);
      // console.log('File to upload:', file);
      
      const authHeaders = await this.getAuthHeaders();
      
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${url}/images/${encodeURIComponent(imageKey)}`, {
        method: 'PUT',
        headers: {
          ...authHeaders
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        return {
          success: true,
          imageKey: result.data.key,
          imageUrl: result.data.location,
          updated: true
        };
      } else {
        throw new Error(result.error || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating image:', error);
      throw error;
    }
  }

  // Delete image using path ID
  async deleteImageByPathId(pathId) {
    const imageKey = `transition-graphs/${pathId}`;
    return this.deleteImage(imageKey);
  }

  // Delete image
  async deleteImage(imageKey) {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${url}/images/${encodeURIComponent(imageKey)}`, {
        method: 'DELETE',
        headers: {
          ...authHeaders
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        return {
          success: true,
          message: 'Image deleted successfully'
        };
      } else {
        throw new Error(result.error || 'Delete failed');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  }
}

export default new ImageService();